import type { CirclesChart } from '@shared/circlesChart';
import type { PlayfieldRect } from '../circlesPlayfieldGeometry';
import type {
  CirclesScorePop,
  CirclesShockwave,
  HitBurst,
} from './circlesRenderTypes';
import {
  HIT_BURST_BODY_END_SCALE,
  HIT_BURST_BODY_LIFE,
  HIT_BURST_MAX,
  HIT_BURST_MAX_LIFE,
  HIT_BURST_RING_END_SCALE,
  HIT_BURST_RING_LIFE,
  SCORE_POP_POOL_MAX,
  SHOCKWAVE_POOL_MAX,
} from './circlesRenderConstants';
import { hexToRgba } from './circlesRenderColour';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Expanding ring overlays. Spawned per hit / cursor tap, eased so they
 * shoot out fast then settle (`1 - (1-t)^3` on radius), with quadratic
 * alpha falloff. The wave LEAPS from the impact point on its first
 * frame — visual equivalent of a "crack" sound.
 */
export class ShockwaveSystem {
  private shockwaves: CirclesShockwave[] = [];
  private quality: RenderQuality;

  constructor(quality: RenderQuality) {
    this.quality = quality;
  }

  /**
   * Append incoming shockwaves to the active animation list. Caps the
   * pool at {@link SHOCKWAVE_POOL_MAX} to bound per-frame stroke
   * calls — a player rapid-firing misses + cursor pulses can otherwise
   * queue dozens before any of them naturally expire.
   */
  ingest(incoming: CirclesShockwave[]): void {
    for (let i = 0; i < incoming.length; i++) {
      this.shockwaves.push(incoming[i]);
    }
    if (this.shockwaves.length > SHOCKWAVE_POOL_MAX) {
      this.shockwaves.splice(0, this.shockwaves.length - SHOCKWAVE_POOL_MAX);
    }
  }

  update(dt: number): void {
    for (let i = this.shockwaves.length - 1; i >= 0; i--) {
      const s = this.shockwaves[i];
      s.life -= dt;
      if (s.life <= 0) {
        this.shockwaves.splice(i, 1);
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.shockwaves.length === 0) {
      return;
    }
    ctx.save();
    // Hoist the shadow check: with up to SHOCKWAVE_POOL_MAX waves
    // alive a per-wave `if` is small but redundant — either the tier
    // wants shadows everywhere or nowhere.
    const useShadow = this.quality.chromeShadows;
    for (let i = 0; i < this.shockwaves.length; i++) {
      const s = this.shockwaves[i];
      const t = 1 - s.life / s.maxLife;
      const eased = 1 - (1 - t) * (1 - t) * (1 - t);
      const r = s.startR + (s.endR - s.startR) * eased;
      const fade = Math.max(0, 1 - t * t);
      const alpha = s.alpha * fade;
      if (alpha <= 0.01) {
        continue;
      }
      ctx.strokeStyle = hexToRgba(s.colour, alpha);
      ctx.lineWidth = s.thickness * (1 - t * 0.4);
      if (useShadow) {
        ctx.shadowColor = s.colour;
        ctx.shadowBlur = 16 * fade;
      }
      ctx.beginPath();
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.shadowBlur = 0;
    ctx.restore();
  }
}

/**
 * Two-layer hit-burst overlays (body + ring) drawn at the centre of
 * recently-resolved circles. Skipped under reduced-motion. Capped at
 * {@link HIT_BURST_MAX} so dense streams can't unbounded-grow.
 */
export class HitBurstSystem {
  private hitBursts: HitBurst[] = [];
  private reducedMotion: boolean;
  private quality: RenderQuality;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
  }

  /**
   * Spawn a hit-burst at `(x, y)` for the given combo colour. No-op
   * under reduced-motion — callers don't need to gate the call.
   */
  spawn(x: number, y: number, colour: string): void {
    if (this.reducedMotion) {
      return;
    }
    this.hitBursts.push({ x, y, colour, age: 0 });
    if (this.hitBursts.length > HIT_BURST_MAX) {
      this.hitBursts.splice(0, this.hitBursts.length - HIT_BURST_MAX);
    }
  }

  update(dt: number): void {
    if (this.hitBursts.length === 0) {
      return;
    }
    for (let i = this.hitBursts.length - 1; i >= 0; i--) {
      const p = this.hitBursts[i];
      p.age += dt;
      if (p.age >= HIT_BURST_MAX_LIFE) {
        this.hitBursts.splice(i, 1);
      }
    }
  }

  /**
   * Render the two-layer hit burst (body + ring) for every recently
   * resolved circle. Each burst draws:
   *
   *   1. **Body** — a filled disc that scales 1.0 → 1.4× the chart's
   *      hit-circle radius over {@link HIT_BURST_BODY_LIFE} seconds,
   *      fading to zero. Cubic ease-out so the pop snaps outward
   *      then settles.
   *
   *   2. **Ring** — a thinner stroked ring that scales 1.0 → 1.5×
   *      over {@link HIT_BURST_RING_LIFE} seconds (faster than the
   *      body), fading. Stand-in for the approach circle "click
   *      burst" — instead of the approach ring vanishing silently
   *      when the player taps, this brief outward pulse confirms
   *      contact.
   */
  draw(
    ctx: CanvasRenderingContext2D,
    chart: CirclesChart,
    field: PlayfieldRect,
  ): void {
    if (this.hitBursts.length === 0) {
      return;
    }
    const baseR = chart.circleRadius * field.scale;
    ctx.save();
    const useShadow = this.quality.chromeShadows;
    for (const p of this.hitBursts) {
      // ---- Body burst (filled disc, longer life, 1.4× peak) ----
      // Body alpha peaks at 0.25 to avoid masking upcoming stacked
      // / streamed circles that drawHitObjects laid down beneath us
      // — at higher peaks the burst's combo-coloured disc visibly
      // tints the freshly-appearing next circle in dense patterns.
      if (p.age < HIT_BURST_BODY_LIFE) {
        const tBody = p.age / HIT_BURST_BODY_LIFE;
        const easeBody = 1 - Math.pow(1 - tBody, 3);
        const bodyR = baseR * (1 + (HIT_BURST_BODY_END_SCALE - 1) * easeBody);
        const bodyAlpha = (1 - easeBody) * 0.25;
        if (bodyAlpha > 0.01) {
          ctx.globalAlpha = 1;
          ctx.fillStyle = hexToRgba(p.colour, bodyAlpha);
          if (useShadow) {
            ctx.shadowColor = p.colour;
            // Tie the shadow radius to the same cubic decay as alpha
            // so we don't pay for a Gaussian blur whose contribution
            // is already invisible — matches the ring layer.
            ctx.shadowBlur = 22 * (1 - easeBody);
          }
          ctx.beginPath();
          ctx.arc(p.x, p.y, bodyR, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      // ---- Ring puff (stroked ring, shorter life, 1.5× peak) ----
      if (p.age < HIT_BURST_RING_LIFE) {
        const tRing = p.age / HIT_BURST_RING_LIFE;
        const easeRing = 1 - Math.pow(1 - tRing, 3);
        const ringR = baseR * (1 + (HIT_BURST_RING_END_SCALE - 1) * easeRing);
        const ringAlpha = 1 - easeRing;
        if (ringAlpha > 0.01) {
          ctx.globalAlpha = ringAlpha;
          ctx.strokeStyle = p.colour;
          ctx.lineWidth = Math.max(1.5, baseR * 0.18) * (1 - tRing * 0.6);
          if (useShadow) {
            ctx.shadowColor = p.colour;
            ctx.shadowBlur = 18 * ringAlpha;
          }
          ctx.beginPath();
          ctx.arc(p.x, p.y, ringR, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
    }
    ctx.shadowBlur = 0;
    ctx.restore();
  }
}

/**
 * Score-pop flyers. Bold "+N" text that arcs from the hit point toward
 * the HUD score, fading and shrinking as it nears the destination.
 * The arc gives the trail a satisfying "thrown" feel rather than a
 * flat slide; the squash at the end suggests the number was
 * "absorbed" into the HUD.
 */
export class ScorePopSystem {
  private scorePops: CirclesScorePop[] = [];
  private reducedMotion: boolean;
  private quality: RenderQuality;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
  }

  ingest(incoming: CirclesScorePop[]): void {
    for (let i = 0; i < incoming.length; i++) {
      this.scorePops.push(incoming[i]);
    }
    if (this.scorePops.length > SCORE_POP_POOL_MAX) {
      this.scorePops.splice(0, this.scorePops.length - SCORE_POP_POOL_MAX);
    }
  }

  update(dt: number): void {
    for (let i = this.scorePops.length - 1; i >= 0; i--) {
      const p = this.scorePops[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.scorePops.splice(i, 1);
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.scorePops.length === 0) {
      return;
    }
    for (let i = 0; i < this.scorePops.length; i++) {
      const p = this.scorePops[i];
      const t = 1 - p.life / p.maxLife;
      // Cubic ease-in on the position so the pop hovers over the hit
      // point briefly before accelerating toward the HUD.
      const eased = t * t * t;
      const x = p.startX + (p.targetX - p.startX) * eased;
      const linearY = p.startY + (p.targetY - p.startY) * eased;
      // Sin-curve arc kicks the pop upward at the start, settles at
      // the target — a subtle hop that makes the trajectory readable.
      const arc = Math.sin(t * Math.PI) * (this.reducedMotion ? 0 : 18);
      const y = linearY - arc;
      // Hold full size for the first 30% of life, then shrink to 0.7×
      // as it approaches the HUD so the absorption reads.
      const scaleProgress = Math.max(0, (t - 0.3) / 0.7);
      const scale = (p.big ? 1.25 : 1) * (1 - scaleProgress * 0.3);
      const alpha = Math.max(0, 1 - eased * 1.4);
      if (alpha <= 0.02) {
        continue;
      }
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(x, y);
      ctx.scale(scale, scale);
      ctx.font = `bold ${p.big ? 28 : 22}px 'Rajdhani', 'Orbitron', system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      // Dark stroke under the coloured glyph so "+300" stays readable
      // when the pop lands inside a same-coloured effect (e.g. a
      // green perfect score-pop inside a green perfect shockwave).
      ctx.lineJoin = 'round';
      ctx.miterLimit = 2;
      ctx.lineWidth = p.big ? 5 : 4;
      ctx.strokeStyle = 'rgba(8, 10, 22, 0.85)';
      ctx.strokeText(p.text, 0, 0);
      if (this.quality.chromeShadows) {
        ctx.shadowColor = p.colour;
        ctx.shadowBlur = p.big ? 22 : 14;
      }
      ctx.fillStyle = p.colour;
      ctx.fillText(p.text, 0, 0);
      ctx.restore();
    }
  }
}
